import { computed, Injectable, inject } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { take } from 'rxjs/operators';

import {
  FilterModel,
  FilterModels,
  ListingDataSource,
  ListingPageModel
} from '../../../shared/components/listing-page/services/listing-page-model/listing-page-model.types';
import { ListingTableComponent } from '../../../shared/components/listing-table/listing-table.component';
import { OrderDirection } from '../../equipment/devices/components/devices.models';

import { ReportGroupsService } from './services/report-groups.service';
import {
  tableConfig,
  reportGroupTableSortFiled,
  statusFilterModel,
  commodityFilterModel,
  applicableFilterModel,
  SEARCH_TYPES
} from './report-groups.constants';
import { ReportGroupsTable, ReportGroups } from './report-groups.types';

import { TableData } from '../../../shared/types/api-response.types';
import { PermissionsService } from '../../../services/permissions/permissions.service';
import { PermissionValue, ResourceValue } from '../../../services/permissions/permissions.types';
import { TranslateRegistry } from 'src/app/services/translate/translate.registry';
import { toSignal } from '@angular/core/rxjs-interop';
import { ListingPageModelAbstract } from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model-abstract';
import { ListingPageModelRegistry } from '@portal/shared/components/listing-page/services/listing-page-model/listing-page-model-registry.service';
import { ListingPageId } from '@portal/shared/components/listing-page/store/listing-page-store.types';
import { ExportCsvConfig } from 'src/app/services/file/export-data.service';
import { Commodity } from '@portal/shared/types/commodity.types';
import { Store } from '@ngrx/store';
import {
  isSingleCommodityClient,
  selectNonCommunicationCommodities
} from 'src/app/pages/help-and-settings/user-settings/store/user-settings.reducer';

@Injectable({ providedIn: 'root' })
export class ReportGroupsListingModelService extends ListingPageModelAbstract implements ListingPageModel {
  private readonly reportGroupsService = inject(ReportGroupsService);
  private readonly permissions = inject(PermissionsService);
  readonly listingPageModelRegistry = inject(ListingPageModelRegistry);
  readonly #store = inject(Store);
  readonly #isSingleCommodityClient = this.#store.selectSignal(isSingleCommodityClient);

  readonly pageTitle = TranslateRegistry.get.REPORT_GROUPS.TITLE.REPORT_GROUPS;
  readonly searchTypeList = computed(() => SEARCH_TYPES);
  readonly selectedSearchType = computed(() => SEARCH_TYPES[0]);
  readonly sortField = reportGroupTableSortFiled;
  readonly sorting = computed(() => ({
    columnName: reportGroupTableSortFiled.groupName,
    orderDirection: OrderDirection.ASC
  }));
  readonly hasEditPermission = toSignal(this.permissions.hasRight(ResourceValue.REPORT_GROUPS, PermissionValue.EDIT));
  readonly tableColumns = computed(() => {
    const isSingleCommodity = this.#isSingleCommodityClient();
    const config = this.hasEditPermission() ? tableConfig : tableConfig.filter(col => col.field !== 'groupUID');
    return config.map(col => (col.field === 'commodity' ? { ...col, isHidden: isSingleCommodity } : col));
  });
  readonly dataSource: ListingDataSource<ReportGroupsTable> = {
    getAll: tableState => this.reportGroupsService.getTableData(tableState),
    total: 0
  };
  readonly tableCmp = ListingTableComponent;

  readonly exportCsv = {
    fileName: 'Export-ReportGroups',
    mapData: (data: TableData<ReportGroups>): ExportCsvConfig[] => {
      return data.data.map(reportGroup => {
        return {
          groupName: { title: 'ReportGroupName', value: reportGroup.groupName },
          status: { title: 'Status', value: reportGroup.groupStatus },
          commodity: {
            title: 'Commodity',
            value: reportGroup.commodity
              ? reportGroup.commodity === Commodity.Electricity
                ? 'Electric'
                : reportGroup.commodity
              : ''
          },
          applicable: { title: 'Applicable', value: reportGroup.applicable ?? '' },
          deviceCount: { title: 'Devices', value: reportGroup.deviceCount },
          updatedOn: { title: 'LastEdited', value: reportGroup.updatedOn },
          updatedBy: { title: 'UpdatedBy', value: reportGroup.updatedBy }
          // Description: reportGroup.groupDescription,
        };
      });
    }
  };
  readonly pageId = ListingPageId.ReportGroups;
  readonly filterSource = {
    getAll: (): Observable<FilterModels> =>
      this.#store.select(selectNonCommunicationCommodities).pipe(
        take(1),
        map(userCommodities => {
          const commodityItems = commodityFilterModel.items.filter(({ value }) =>
            userCommodities.includes(value as string)
          );
          const dynamicCommodityFilter: FilterModel = {
            ...commodityFilterModel,
            items: commodityItems,
            selected: commodityItems
          };
          return [
            statusFilterModel,
            ...(this.#isSingleCommodityClient() ? [] : [dynamicCommodityFilter]),
            applicableFilterModel
          ];
        })
      ),
    getReportGroup: (): Observable<FilterModel> => of()
  };
  constructor() {
    super();
    this.listingPageModelRegistry.add(this.pageId, this);
  }
}
